// npm packages
import { React, useContext, useState, useEffect } from "react";
import * as moment from "moment";
import { toast } from "react-toastify";
// custom pages
import Context from "context/context";
import vraShiftsAPI from "apis/panel/vraShifts/vraShifts";
import QualityAnalyzerAPI from "apis/panel/qualityAnalyzer/qualityAnalyzer";

// material ui
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Button from "@mui/material/Button";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import Autocomplete from "@mui/material/Autocomplete";

const Accession = ({ setDisableDropdown }) => {
  const { dispatch } = useContext(Context);
  const { getRPAllowanceReportAPI } = vraShiftsAPI();
  const { getMasterDataAPI, getAccessionDetailsExport } = QualityAnalyzerAPI();

  const [masterData, setMasterData] = useState([]);

  const initialStartDate = new Date(moment().subtract(1, "days"));
  const initialEndDate = new Date();
  const [exportObj, setExportObj] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
    practiceIds: "",
    radiologistIds: "",
    modalityIds: "",
  });

  const handleGetMasterData = async () => {
    try {
      const res = await getMasterDataAPI();
      if (res?.data) setMasterData(res?.data);
    } catch (err) {}
  };

  useEffect(() => {
    handleGetMasterData();
  }, []);

  const handleExport = async () => {
    let body = {};
    if (!exportObj?.startDate) {
      return toast.error('Please select valid start .', {
        toastId: "start date error",
      });
    }
    if (!exportObj?.endDate) {
      return toast.error('Please select valid end dates.', {
        toastId: " end date error",
      });
    }

    if(new Date(exportObj.startDate).getTime()>new Date(exportObj.endDate).getTime()){
      return toast.error('Please select valid start and end dates.', {
             toastId: "start and end date error",
           });
   }
    body = {
      startDate: moment(exportObj?.startDate).format("YYYY-MM-DD"),
      endDate: moment(exportObj?.endDate).format("YYYY-MM-DD"),
    };

    if (exportObj?.practiceIds && exportObj?.practiceIds.length > 0) {
      body = {
        ...body,
        practiceIds: exportObj?.practiceIds?.map((p) => p.practiceName),
      };
    }
    if (exportObj?.radiologistIds && exportObj?.radiologistIds.length > 0) {
      body = {
        ...body,
        radiologistIds: exportObj?.radiologistIds?.map(
          (p) => p.radiologistName
        ),
      };
    }
    if (exportObj?.modalityIds && exportObj?.modalityIds?.length > 0) {
      body = {
        ...body,
        modalityIds: exportObj?.modalityIds.map((e) => e.modalitName),
      };
    }
    dispatch({ type: "SET_LOADING", payload: true });

    await getAccessionDetailsExport(body);
    dispatch({ type: "SET_LOADING", payload: false });
  };

  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');

  const handleStartDate = (value) => {
    // Convert both dates to timestamps (milliseconds)
    if (value && exportObj.endDate && new Date(value).getTime() > new Date(exportObj.endDate).getTime()) {
      setStartDateError('Start date cannot be later than end date');
      setExportObj({ ...exportObj, startDate: value });
    } else {
      setStartDateError('');
      setExportObj({ ...exportObj, startDate: value });
    }
  };

  const handleEndDate = (value) => {
    // Convert both dates to timestamps (milliseconds)
    if (value && exportObj.startDate && new Date(value).getTime() < new Date(exportObj.startDate).getTime()) {
      setEndDateError('End date cannot be earlier than start date');
      setExportObj({ ...exportObj, endDate: value });
    } else {
      setEndDateError('');
      setExportObj({ ...exportObj, endDate: value });
    }
  };

  return (
    <div>
      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Box className="label">
            Start Date <span style={{ color: "red" }}>*</span>
          </Box>
          <LocalizationProvider fullWidth dateAdapter={AdapterDateFns}>
            <DatePicker
              id="startdate"
              name="startdate"
              sx={{ width: "100%" }}
              format="dd/MM/yyyy"
              renderInput={(params) => <TextField {...params} fullWidth />}
              maxDate={exportObj?.endDate}
              value={exportObj?.startDate}
              onChange={(newValue) => handleStartDate(newValue)}
            />
          </LocalizationProvider>
          {startDateError && (
        <Box sx={{ color: 'red', fontSize: '13px' }}>
          <span>*</span> {startDateError}
        </Box>
      )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Box className="label">
            End Date <span style={{ color: "red" }}>*</span>
          </Box>
          <LocalizationProvider fullWidth dateAdapter={AdapterDateFns}>
            <DatePicker
              id="enddate"
              name="enddate"
              sx={{ width: "100%" }}
              maxDate={new Date()}
              format="dd/MM/yyyy"
              renderInput={(params) => <TextField {...params} fullWidth />}
              minDate={exportObj?.startDate}
              value={exportObj?.endDate}
              onChange={(newValue) => handleEndDate(newValue)}
            />
          </LocalizationProvider>
          {endDateError && (
        <Box sx={{ color: 'red', fontSize: '13px' }}>
          <span>*</span> {endDateError}
        </Box>
      )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Box className="label">Practice</Box>
          <Autocomplete
            multiple
            options={masterData.practice || []}
            getOptionLabel={(option) => option?.practiceName || ""}
            value={exportObj?.practiceIds || []}
            onChange={(event, newValues) => {
              setExportObj({ ...exportObj, practiceIds: newValues });
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Box className="label">Radiologist</Box>
          <Autocomplete
            multiple
            options={masterData.radiologist || []}
            getOptionLabel={(option) => option?.radiologistName || ""}
            value={exportObj?.radiologistIds || []}
            onChange={(event, newValues) => {
              setExportObj({ ...exportObj, radiologistIds: newValues });
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Box className="label">Modality</Box>
          <Autocomplete
            multiple
            options={masterData.modality || []}
            getOptionLabel={(option) => option?.modalitName || ""}
            value={exportObj?.modalityIds || []}
            onChange={(event, newValues) => {
              setExportObj({ ...exportObj, modalityIds: newValues });
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12} style={{ textAlign: "right", padding: "20px 0px" }}>
          <Button
            className="download-btn"
            startIcon={
              <DownloadForOfflineIcon
                style={{ fontSize: 30, verticalAlign: "middle" }}
              />
            }
            onClick={handleExport}
          >
            Export
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default Accession;
